/**
 * Rogue Alloy accessory shop (design/accessories-and-upsell-2026-09-26.md,
 * package B, "acc-theme-navigation").
 *
 * Pure functions live at the top (unit tested by tests/rogue-accessories.test.js,
 * following the same pattern as tests/rogue-tools.test.js); the DOM wiring at
 * the bottom only reads settled state and writes it back into the page. No
 * third-party libraries, no bundler: loaded directly as an ES module from
 * sections/rogue-accessory-shop.liquid.
 *
 * This file does not import from assets/rogue-fitment.js: that module is
 * owned by the fitment/tyre workflow (see theme/README.md and the package
 * brief's DO NOT TOUCH list) and this module has no need of its vehicle
 * selector or PCD matching internals - only the same small `rogue.vehicle`
 * localStorage/`rogue_vehicle` cookie contract every package reads.
 */

/* -------------------------------------------------------------------------
 * Pure functions
 * ---------------------------------------------------------------------- */

/** The three title suffixes the compact card strips by default (design spec 3.4). */
export const DEFAULT_TITLE_SUFFIXES = ' for van alloy wheels| for Rogue alloys| for vans';

/**
 * Removes a trailing search-friendly suffix from a product title for the
 * compact card ("Black locking wheel nuts for van alloy wheels" -> "Black
 * locking wheel nuts"). Only strips a suffix that is genuinely at the end of
 * the string, so a suffix that happens to be a substring elsewhere in the
 * title is left alone; the first matching suffix (in list order) wins.
 * @param {string} title
 * @param {string} [suffixes] - a pipe-separated list, e.g. ' for van alloy wheels| for Rogue alloys'
 * @returns {string}
 */
export function stripCardTitle(title, suffixes) {
  const source = typeof title === 'string' ? title : '';
  const suffixSource = typeof suffixes === 'string' && suffixes.trim() !== '' ? suffixes : DEFAULT_TITLE_SUFFIXES;

  let result = source;
  for (const part of suffixSource.split('|')) {
    const suffix = part.trim();
    if (suffix === '') continue;
    if (result.length > suffix.length && result.endsWith(suffix)) {
      result = result.slice(0, result.length - suffix.length);
      break;
    }
  }
  return result.trim();
}

/**
 * Splits a comma-separated setting (fallback tags, the TPMS van list) into a
 * clean array, dropping blank entries. Never throws on non-string input.
 * @param {string|null|undefined} text
 * @returns {string[]}
 */
export function parseTagList(text) {
  if (typeof text !== 'string' || text.trim() === '') return [];
  return text
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
}

/** The fixed candidate order the "for your van" band always shows in (design spec 3.6/4.4). */
export const VAN_BAND_CANDIDATE_ORDER = ['tpms', 'brace', 'rinse'];

/**
 * Decides which of the "for your van" band's compact cards to show, and in
 * which order: TPMS only when the stored van's slug is in the TPMS list,
 * then the wheel brace, then Rogue Rinse - available candidates only (design
 * spec 3.6). `candidates` are keyed by the same three names; a candidate
 * missing from the array, or present with `available: false`, is dropped.
 * @param {{ slug?: string, tpmsVans?: string|string[], candidates?: Array<{key: string, available?: boolean}> }} [input]
 * @returns {Array<{key: string, available?: boolean}>}
 */
export function vanBandPicks({ slug, tpmsVans, candidates } = {}) {
  const tpmsList = Array.isArray(tpmsVans)
    ? tpmsVans.map((entry) => String(entry).trim()).filter((entry) => entry !== '')
    : parseTagList(typeof tpmsVans === 'string' ? tpmsVans : '');
  const slugValue = typeof slug === 'string' ? slug.trim() : '';
  const list = Array.isArray(candidates) ? candidates : [];
  const byKey = new Map(list.filter((entry) => entry && typeof entry.key === 'string').map((entry) => [entry.key, entry]));

  const picks = [];
  for (const key of VAN_BAND_CANDIDATE_ORDER) {
    if (key === 'tpms' && !tpmsList.includes(slugValue)) continue;
    const candidate = byKey.get(key);
    if (candidate && candidate.available !== false) picks.push(candidate);
  }
  return picks.slice(0, 3);
}

/**
 * Design spec 3.2: the chip row becomes a 240px left sidebar at 990px and
 * wider once there are more category blocks than the section's
 * `sidebar_threshold` setting allows comfortably in a single row.
 * @param {number} count - the number of category blocks
 * @param {number} threshold - the section's `sidebar_threshold` setting
 * @returns {boolean}
 */
export function useSidebar(count, threshold) {
  const countValue = Number(count);
  const thresholdValue = Number(threshold);
  if (!Number.isFinite(countValue) || !Number.isFinite(thresholdValue)) return false;
  return countValue > thresholdValue;
}

/**
 * Parses the shopper's stored vehicle from raw localStorage/cookie string
 * values, storage first (design spec 3.6): only `slug` and `display_name`
 * matter to this package. Never throws: malformed or missing input returns
 * null, exactly like assets/rogue-fitment.js's own deserializeVehicle - kept
 * as a small, self-contained copy here rather than an import, so this
 * package has no dependency on that file's internals.
 * @param {string|null|undefined} storageValue
 * @param {string|null|undefined} cookieValue
 * @returns {{slug: string, display_name: string}|null}
 */
export function readStoredVehicle(storageValue, cookieValue) {
  return parseStoredVehicleJson(storageValue) || parseStoredVehicleJson(cookieValue);
}

/**
 * @param {string|null|undefined} raw
 * @returns {{slug: string, display_name: string}|null}
 */
function parseStoredVehicleJson(raw) {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || typeof parsed.slug !== 'string' || parsed.slug === '') return null;
    return {
      slug: parsed.slug,
      display_name: typeof parsed.display_name === 'string' ? parsed.display_name : '',
    };
  } catch {
    return null;
  }
}

/**
 * Fills the "For your [van]" heading/body templates in with the stored
 * van's display name. Placeholders are the literal string `[van]` (never
 * braces inside `{{ }}` - see theme/README.md), and every occurrence is
 * replaced, not just the first.
 * @param {string} template
 * @param {string} vanName
 * @returns {string}
 */
export function fillVanTemplate(template, vanName) {
  const source = typeof template === 'string' ? template : '';
  const name = typeof vanName === 'string' ? vanName : '';
  return source.split('[van]').join(name);
}

/* -------------------------------------------------------------------------
 * DOM wiring - guarded so this module still imports cleanly in Node
 * (tests/rogue-accessories.test.js), where `customElements`/`document` do
 * not exist.
 * ---------------------------------------------------------------------- */

if (typeof document !== 'undefined') {
  const ADDED_LABEL_MS = 3000;

  /**
   * The Rogue accessory shop section root: wires up every "Add" button
   * (design spec 4.4's ghost "Add", both the shelf tile and the "for your
   * van" band's compact cards) and reveals the "for your van" band once a
   * vehicle is stored client side.
   *
   * The add-to-basket flow mirrors assets/rogue-fitment.js's own
   * RogueAccessoryCrossSell exactly (read there, not edited here): POST
   * /cart/add.js, re-read /cart.js, then dispatch `@shopify/events`'
   * CartLinesUpdateEvent so the header bubble and cart drawer refresh with
   * no reload - only `detail.source` differs ('rogue-accessories').
   */
  class RogueAccessoryShop extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => this.handleClick(event));
      this.revealVanBand();
    }

    /** @param {MouseEvent} event */
    handleClick(event) {
      const target = /** @type {HTMLElement} */ (event.target);
      const button = target?.closest?.('[data-rogue-accessory-add]');
      if (!button || button.hasAttribute('disabled')) return;
      this.addAccessory(/** @type {HTMLButtonElement} */ (button));
    }

    /** @param {HTMLButtonElement} button */
    async addAccessory(button) {
      const variantId = button.getAttribute('data-rogue-variant-id');
      if (!variantId) return;

      const label = button.querySelector('[ref="addLabel"]');
      const originalText = label ? label.textContent : '';
      button.setAttribute('disabled', '');
      if (label) label.textContent = 'Adding…';

      try {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        const addJson = await addResponse.json();
        if (!addResponse.ok || addJson?.status) {
          throw new Error(addJson?.description || addJson?.message || 'Could not add to basket');
        }

        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cartJson = await cartResponse.json();

        await this.announceCartUpdate(cartJson, variantId);

        if (label) label.textContent = 'Added';
        setTimeout(() => {
          button.removeAttribute('disabled');
          if (label) label.textContent = originalText;
        }, ADDED_LABEL_MS);
      } catch (error) {
        console.warn('[rogue-accessories] add to cart failed:', error);
        if (label) label.textContent = 'Try again';
        button.removeAttribute('disabled');
      }
    }

    /**
     * @param {Record<string, unknown>} cartJson - the parsed /cart.js response
     * @param {string} variantId
     */
    async announceCartUpdate(cartJson, variantId) {
      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: Promise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
              detail: {
                items: cartJson.items,
                source: 'rogue-accessories',
                itemCount: cartJson.item_count,
              },
            }),
          })
        );
      } catch (error) {
        // '@shopify/events' failed to load (offline, CDN hiccup) - the
        // accessory is still in the basket; only the live bubble/drawer
        // refresh is skipped, matching RogueAccessoryCrossSell's own fallback.
        console.warn('[rogue-accessories] cart update event skipped:', error);
      }
    }

    /**
     * Design spec 3.6: the band is server-rendered hidden and only revealed
     * once a vehicle is actually stored - localStorage first, the
     * `rogue_vehicle` cookie otherwise. Every storage/cookie read is
     * wrapped in try/catch (private browsing, disabled storage).
     */
    revealVanBand() {
      const band = this.querySelector('[data-rogue-van-band]');
      if (!band) return;

      const vehicle = readStoredVehicle(this.safeReadStorage(), this.safeReadCookie());
      if (!vehicle) return;

      const heading = band.querySelector('[ref="vanBandHeading"]');
      if (heading) heading.textContent = fillVanTemplate(band.getAttribute('data-heading-template') || '', vehicle.display_name);

      const body = band.querySelector('[ref="vanBandBody"]');
      if (body) body.textContent = fillVanTemplate(band.getAttribute('data-body-template') || '', vehicle.display_name);

      const changeLink = band.querySelector('[ref="vanBandChange"]');
      if (changeLink) {
        const returnPath = `${window.location.pathname}${window.location.search}`;
        changeLink.setAttribute('href', `/pages/vehicles?return=${encodeURIComponent(returnPath)}`);
      }

      const tpmsVans = band.getAttribute('data-tpms-vans') || '';
      const cardEls = Array.from(band.querySelectorAll('[data-rogue-band-card]'));
      const candidates = cardEls.map((el) => ({ key: el.getAttribute('data-rogue-band-card'), available: true }));
      const picks = vanBandPicks({ slug: vehicle.slug, tpmsVans, candidates });
      const pickedKeys = new Set(picks.map((pick) => pick.key));
      cardEls.forEach((el) => {
        el.hidden = !pickedKeys.has(el.getAttribute('data-rogue-band-card'));
      });

      band.hidden = false;
    }

    safeReadStorage() {
      try {
        return window.localStorage.getItem('rogue.vehicle');
      } catch {
        return null;
      }
    }

    safeReadCookie() {
      try {
        const raw = document.cookie
          .split(';')
          .map((part) => part.trim())
          .find((part) => part.startsWith('rogue_vehicle='))
          ?.slice('rogue_vehicle='.length);
        return raw ? decodeURIComponent(raw) : null;
      } catch {
        return null;
      }
    }
  }

  if (typeof customElements !== 'undefined' && !customElements.get('rogue-accessory-shop')) {
    customElements.define('rogue-accessory-shop', RogueAccessoryShop);
  }
}
